import { Fragment, useState } from 'react';
import { useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import API from '../api';

import Loader from '../components/Loader';
import notify from '../components/Notification/helper';
import { chooseInputComponent } from '../common/utils/components';
import PropTypes from 'prop-types';

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function BrandSettingsForm({ onUpdated = () => {} }) {
  const retailer = useSelector(state => state.retailer);
  
  const [retailerPayload, setRetailerPayload] = useState({
    brand_color: retailer.brand_color,
    brand_color_2: retailer.brand_color_2,
    button_background_color: retailer.button_background_color,
    icon_color: retailer.icon_color,
    site_background_color: retailer.site_background_color,
    header_font: retailer.header_font,
    body_font: retailer.body_font,
    button_font: retailer.button_font,
  });

  const [loading, setLoading] = useState(false);

  const handleInputChange = (field, value) => {
    let keyValuePair = {};
    keyValuePair[field] = value;

    setRetailerPayload({
      ...retailerPayload,
      ...keyValuePair
    })
  };

  const fontOptions = [
    'Oswald',
    'Roboto',
    'Montserrat',
    'Outfit',
    'Quicksand',
    'Merriweather',
  ];

  const formSections = [
    {
      title: 'Brand Colors',
      fields: [
        {
          label: 'Brand Color',
          description: 'This will be used as the background color of the second navigation bar and throughout the gateway as an accent color.',
          name: 'brand_color',
          type: 'colour',
          value: retailerPayload.brand_color,
          handleInputChange: handleInputChange,
        },
        {
          label: 'Brand Color 2',
          description: 'This will be used as the heading background color of the link cards on the homepage.',
          name: 'brand_color_2',
          type: 'colour',
          value: retailerPayload.brand_color_2,
          handleInputChange: handleInputChange,
        },
      ]
    },
    {
      title: 'Theme Colors',
      fields: [
        {
          label: 'Button Color',
          description: 'Color to be used as the button background color.',
          name: 'button_background_color',
          type: 'colour',
          value: retailerPayload.button_background_color,
          handleInputChange: handleInputChange,
        },
        {
          label: 'Icon Color',
          description: 'Color to be used for all icons.',
          name: 'icon_color',
          type: 'colour',
          value: retailerPayload.icon_color,
          handleInputChange: handleInputChange,
        },
        {
          label: 'Background Color',
          description: 'Color to be used as the gateway background.',
          name: 'site_background_color',
          type: 'colour',
          value: retailerPayload.site_background_color,
          handleInputChange: handleInputChange,
        },
      ]
    },
    {
      title: 'Font Settings',
      fields: [
        {
          label: 'Header Font',
          description: 'HEAVYWEIGHT HEADINGS FONTS FROM A PRE-APPROVED LIST. FONT PREVIEWS AT FONTS.GOOGLE.COM. TO USE YOUR BRAND FONT, CONTACT US.',
          name: 'header_font',
          type: 'font',
          value: retailerPayload.header_font,
          handleInputChange: handleInputChange,
          options: fontOptions,
        },
        {
          label: 'Body Font',
          description: 'PRE-APPROVED FONTS FROM FONTS.GOOGLE.COM APPEAR IN THIS LIST. TO REQUEST ADDITIONAL FONTS, CONTACT US',
          name: 'body_font',
          type: 'font',
          value: retailerPayload.body_font,
          handleInputChange: handleInputChange,
          options:  fontOptions,
        },
        {
          label: 'Button Font',
          description: 'THIN LEGIBLE FROM FONTS.GOOGLE.COM APPEAR IN THIS LIST. TO REQUEST ADDITIONAL FONTS, CONTACT US',
          name: 'button_font',
          type: 'font',
          value: retailerPayload.button_font,
          handleInputChange: handleInputChange,
          options:  fontOptions,
        },
      ]
    },
  ]

  const makeUpdateRetailerRequest = async () => {
    try {
      // setLoading(true);

      let payload = {
        ...retailerPayload,
      };

      await API.updateRetailer(payload);
      onUpdated();
      notify('success', 'Retailer updated');
    } catch (error) {
      console.log('error: ', error);
      notify('error', 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <Box 
      sx={{
        flexGrow: 1,
      }}
    >
      <Grid container>
        <Grid item xs={12} md={7}>
          {formSections.map((section) => (
            <Box key={section.title}>
              {
                section.title &&
                <Typography
                  className='form-section-title'
                  variant="h4"
                >
                  {section.title}
                </Typography>
              }

              <Grid container spacing={2}>
                {section.fields.map(field => (
                  <Fragment key={field.name}>
                    <Grid
                      key={field.name}
                      item
                      {
                        ...(['colour'].includes(field?.type) ? {
                            xs: 12,
                            md: 4,
                          } : {
                            xs: 12,
                          }
                        )
                      }
                    >
                      { chooseInputComponent(field) }
                    </Grid>
                  </Fragment>
                ))}
              </Grid>
            </Box>
          ))}
        </Grid>

        <Grid item xs={12} md={7} >
          <Button
            disabled={loading}
            className='form-button'
            sx={{ width: '40%', my: 5 }}
            size="large"
            variant="contained"
            onClick={makeUpdateRetailerRequest}
          >
            {loading ? 'Updating... ' : 'Update' }
          </Button>
        </Grid>
      </Grid>
    </Box>
  )
}

export default BrandSettingsForm